import type { DeskDetail, DeskLearning, LearnedFrom } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BookOpen, GraduationCap, Sparkles, Tags } from 'lucide-react';
import type { ReactNode } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { api } from '@/lib/api';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const SUBJECT: Record<DeskLearning['subject'], { label: string; icon: ReactNode }> = {
  playbook: { label: t('Playbook'), icon: <BookOpen /> },
  desk_skill: { label: t('Skill'), icon: <Sparkles /> },
  work_item_type: { label: t('Kind of work'), icon: <Tags /> },
};

const FROM: Record<LearnedFrom, string> = {
  case: t('from a case'),
  answer: t('from an answer'),
  correction: t('from a correction'),
  assumption: t('assumed'),
  observation: t('observed'),
  report: t('after a report'),
};

/** What the desk learned, when and why (docs/features/desk-training.md#learning-log). */
export function DeskLearningsSection({ desk }: { desk: DeskDetail }) {
  const learnings = useQuery({ queryKey: qk.deskLearnings(desk.id), queryFn: () => api.desks.learnings(desk.id) });
  return (
    <Section
      icon={<GraduationCap />}
      title={t('What the desk learned')}
      description={t('Every playbook, skill and kind of work written for this desk: what it learned, when, and what showed it.')}
    >
      {learnings.isPending ? (
        <LoadingRows rows={3} />
      ) : learnings.error ? (
        <ErrorState error={learnings.error} onRetry={() => void learnings.refetch()} />
      ) : !learnings.data.length ? (
        <EmptyState compact title={t('Nothing learned yet')} description={t('Start a training, and what the desk learns shows up here.')} />
      ) : (
        <ol className="flex flex-col gap-3">
          {learnings.data.map((l) => (
            <li key={l.id} className="flex gap-3 text-sm">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300 [&_svg]:size-3.5">
                {SUBJECT[l.subject].icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                  <span className="font-medium">
                    {SUBJECT[l.subject].label} „{l.name}“{l.version ? ` v${l.version}` : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <RelativeTime value={l.createdAt} />
                    {l.by ? ` · ${l.by}` : ''}
                    {l.learnedFrom ? ` · ${FROM[l.learnedFrom]}` : ''}
                  </span>
                </div>
                <p className="mt-0.5 whitespace-pre-line">{l.what}</p>
                {l.why && <p className="mt-0.5 whitespace-pre-line text-muted-foreground">{tr('Why: {why}', { why: l.why })}</p>}
                {l.workItem && (
                  <Link
                    to="/w/$workspaceId/work-items/$workItemId"
                    params={{ workspaceId: desk.workspaceId, workItemId: l.workItem.id }}
                    className="mt-0.5 inline-block text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    {l.workItem.title}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}
